import { MouseEventHandler, useEffect, useState } from "react"
import DatePicker from 'react-datepicker'
import { useDispatch, useSelector } from "react-redux"
import { FormInput } from "../components/custom/components"
import DeleteConfirmPurchaseReturn from "../components/DeleteConfirmationPurchaseReturn"
import Modal from "../components/Modal"
import { FormType, FormTypeList } from "../config"
import { getDate, LocalFormatDate } from "../config/functions"
import { AccountHolderType } from "../redux/actions/accountHolder"
import { AuthUserType } from '../redux/actions/authUserActions'
import { fetchPurchaseReturnDateAction, PurchaseReturnType } from "../redux/actions/purchaseReturnAction"
import { AppDispatch, StoreState } from "../redux/store"

interface TableBody {
    sno: number
    invoiceDate: string
    invoiceNo: string
    purchaseReturnEntryNo: string
    supplier: string
    finalAmount: string
    rolePermissions?: any
    onEdit: MouseEventHandler<HTMLButtonElement>
    onDelete: MouseEventHandler<HTMLButtonElement>
}

type HandleEdit = (value: PurchaseReturnType) => void
type HandleDelete = (value: PurchaseReturnType) => void

interface Props {
    setFormType: React.Dispatch<React.SetStateAction<FormType>>
    setId: React.Dispatch<React.SetStateAction<number | undefined>>
    onclose: () => void
    /** When the view modal opens, refetch purchase returns for the selected filters */
    viewOpen?: boolean
}

const toApiDate = (date: Date | null): string => getDate(date ?? new Date())

const PurchaseReturn = (props: Props) => {
    const dispatch = useDispatch<AppDispatch>()
    const purchaseList = useSelector<StoreState, PurchaseReturnType[]>(state => state.purchaseReturn)
    const AuthUser = useSelector<StoreState, AuthUserType | null>(state => state.authUser);
    const AccountLists = useSelector<StoreState, AccountHolderType[]>(state => state.account)

    const [showDeleteForm, setShowDeleteForm] = useState<boolean>(false)
    const [editData, setEditData] = useState<PurchaseReturnType>()

    const [searchDate, setSearchDate] = useState<Date | null>(new Date());
    const [searchSupplier, setSearchSupplier] = useState<string>('');
    const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>('');
    const url = window.location.pathname
    const rolePermissions = AuthUser?.roles?.find(r => r.pageUrl === url)

    const loadPurchaseReturns = (
        date: Date | null,
        invoiceNo = searchInvoiceNo,
        supplier = searchSupplier,
    ) => {
        const apiDate = toApiDate(date)
        dispatch(fetchPurchaseReturnDateAction(apiDate, apiDate, {
            invoiceNo,
            supplier,
        }))
    }

    useEffect(() => {
        if (props.viewOpen === false) return
        const timer = window.setTimeout(() => {
            loadPurchaseReturns(searchDate, searchInvoiceNo, searchSupplier)
        }, searchInvoiceNo || searchSupplier ? 300 : 0)
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.viewOpen, searchDate, searchInvoiceNo, searchSupplier, dispatch])

    const handleEdit: HandleEdit = (value) => {
        props.setFormType(FormTypeList.EDIT)
        props.setId(value.id)
        setEditData(value)
        props.onclose()
    }

    const handleDelete: HandleDelete = (value) => {
        props.setFormType(FormTypeList.DELETE)
        setEditData(value)
        setShowDeleteForm(true)
    }

    const handleDeleteFormClose = () => {
        setShowDeleteForm(false)
        loadPurchaseReturns(searchDate)
    }

    const handleDateFilter = (date: Date | null) => {
        setSearchDate(date ?? new Date());
    };

    const handleSupplierFilter = (value: string) => {
        setSearchSupplier(value);
    };

    const handleInvoiceNoFilter = (value: string) => {
        setSearchInvoiceNo(value);
    };

    return <>
        <Modal
            headerText={'Delete PurchaseReturn'}
            visible={showDeleteForm}
            onClose={handleDeleteFormClose}
            centered
            size='sm'
        >
            <DeleteConfirmPurchaseReturn
                editData={editData}
                onClose={handleDeleteFormClose}
            />
        </Modal>
        <div className="row">
            <div className="col-lg-12">
                <div className="hstack gap-4 mb-3">
                    <div>
                        <label htmlFor="searchDate">Return Date :</label>
                        <DatePicker
                            id="searchDate"
                            selected={searchDate}
                            onChange={(date) => handleDateFilter(date)}
                            dateFormat="dd-MM-yyyy"
                        />
                    </div>
                    <FormInput
                        name='searchInvoiceNo'
                        label='Invoice Return No :'
                        value={searchInvoiceNo}
                        onChange={(e) => handleInvoiceNoFilter(e.target.value)}
                    />
                    <FormInput
                        name='searchSupplier'
                        label='Supplier :'
                        value={searchSupplier}
                        onChange={(e) => handleSupplierFilter(e.target.value)}
                    />
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-lg-12">
                <div className="card shadow-lg">
                    <div className='table-wrapper'>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th className='text-truncate align-middle'>S.No</th>
                                    <th className='text-truncate align-middle'>Purchase Return Entry No</th>
                                    <th className='text-truncate align-middle'>Invoice Return Date</th>
                                    <th className='text-truncate align-middle'>Invoice Return No</th>
                                    <th className='text-truncate align-middle'>Supplier</th>
                                    <th className='text-truncate align-middle'>Grand Total</th>
                                    <th className='text-truncate align-middle'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchaseList.map((tr, i) => {
                                    let sobj = AccountLists?.find(s => s?.accountNumber === tr?.supplierId)
                                    let sname = sobj?.accountName ? sobj?.accountName : ''
                                    return <UserBody
                                        sno={i + 1}
                                        invoiceNo={tr.invoiceReturnNumber?.toString()}
                                        invoiceDate={tr.invoiceReturnDate?.toString()}
                                        purchaseReturnEntryNo={tr.purchaseReturnEntryNumber?.toString()}
                                        finalAmount={tr.grandTotal ? tr.grandTotal?.toString() : '0'}
                                        supplier={sname}
                                        onEdit={() => handleEdit(tr)}
                                        onDelete={() => handleDelete(tr)}
                                        rolePermissions={rolePermissions}
                                        key={i}

                                    />
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </>
}

const UserBody = ({ sno, invoiceDate, finalAmount, invoiceNo, purchaseReturnEntryNo, supplier, onEdit, onDelete, rolePermissions }: TableBody) => {
    return <tr key={sno}>
        <td className='align-middle'>{sno}</td>
        <td className='text-capitalize align-middle'>{purchaseReturnEntryNo}</td>
        <td className='text-capitalize align-middle'>{LocalFormatDate(invoiceDate)}</td>
        <td className='text-capitalize align-middle'>{invoiceNo}</td>
        <td className='text-capitalize align-middle'>{supplier}</td>
        <td className='text-capitalize align-middle'>{finalAmount}</td>
        <td className="align-middle">
            {(rolePermissions?.edit || rolePermissions?.delete) && <div className='hstack gap-1'>
                {rolePermissions?.edit && <button className='btn btn-sm fw-bold btn-info p-1' onClick={onEdit}><i className="fe-edit noti-icon"></i></button>}
                {rolePermissions?.delete && <button className='btn btn-sm fw-bold btn-danger p-1' onClick={onDelete}><i className="fe-trash-2 noti-icon"></i></button>}
            </div>}
        </td>
    </tr>
}
export default PurchaseReturn
